/**
 * Shape of an individual error entry returned by GraphQL server endpoints.
 */
export interface GraphQLErrorDetail {
  message: string;
  locations?: Array<{ line: number; column: number }>;
  path?: Array<string | number>;
  extensions?: {
    code?: string;
    userMessage?: string;
    response?: {
      code?: string;
      message?: string;
    };
    [additionalFieldKey: string]: unknown;
  };
}

/**
 * Custom Error wrapper representing GraphQL request failures with structured details.
 */
export class GraphQLClientError extends Error {
  public readonly graphQLErrors: GraphQLErrorDetail[];

  constructor(errorMessage: string, graphQLErrors: GraphQLErrorDetail[] = []) {
    super(errorMessage);
    this.name = 'GraphQLClientError';
    this.graphQLErrors = graphQLErrors;
    Object.setPrototypeOf(this, GraphQLClientError.prototype);
  }
}

/**
 * Constructs a GraphQLClientError from an array of server GraphQL errors.
 *
 * Description:
 *   Extracts the primary error message and attaches the complete error collection.
 *
 * Parameters:
 *   rawErrorsArray: GraphQLErrorDetail[] - Array of errors returned in the response envelope.
 *
 * Return Value:
 *   GraphQLClientError - Structured error instance.
 *
 * Exceptions & Errors:
 *   None.
 */
export function createGraphqlClientError(
  rawErrorsArray: GraphQLErrorDetail[],
): GraphQLClientError {
  const primaryErrorMessage =
    rawErrorsArray?.[0]?.message || 'GraphQL operation returned errors.';
  return new GraphQLClientError(primaryErrorMessage, rawErrorsArray);
}

/**
 * Normalizes any error object into a GraphQLClientError instance.
 *
 * Description:
 *   Preserves existing GraphQLClientError instances or wraps generic Error objects.
 *
 * Parameters:
 *   encounteredError: unknown - The caught error object.
 *
 * Return Value:
 *   GraphQLClientError - Standardized GraphQLClientError instance.
 *
 * Exceptions & Errors:
 *   None.
 */
export function toGraphqlClientError(
  encounteredError: unknown,
): GraphQLClientError {
  if (encounteredError instanceof GraphQLClientError) {
    return encounteredError;
  }

  if (encounteredError instanceof Error) {
    return new GraphQLClientError(encounteredError.message, [
      { message: encounteredError.message },
    ]);
  }

  const fallbackMessage =
    typeof encounteredError === 'string'
      ? encounteredError
      : 'An unexpected GraphQL error occurred.';
  return new GraphQLClientError(fallbackMessage, [
    { message: fallbackMessage },
  ]);
}

/**
 * Logs developer diagnostic details in console during local or development runs.
 *
 * Description:
 *   Outputs formatted debug context when running in non-production environments.
 *
 * Parameters:
 *   diagnosticLabel: string - Contextual tag identifying the logged operation.
 *   diagnosticPayload: unknown - Detailed data payload or error object.
 *
 * Return Value:
 *   void.
 *
 * Exceptions & Errors:
 *   None.
 */
export function logOrderDevDetail(
  diagnosticLabel: string,
  diagnosticPayload: unknown,
): void {
  console.debug(`[OrderModule:${diagnosticLabel}]`, diagnosticPayload);
}
